import { useWgClientControllerEngagement } from 'src/apis/backend/generated/white-glove-client/white-glove-client';

// ----------------------------------------------------------------------

/**
 * Whether there is a contract behind this brand to commission work against.
 *
 * The free designs commission nothing: a trial is two emails and then a
 * decision, so a doorway that offers to build a footer on one is offering
 * something the composer would refuse. The engagement is the only place that
 * distinction is written down, so every entry point reads it here rather than
 * each deciding what "managed" means.
 */
export function isManagedEngagement(
  engagement?: { kind?: string; status?: string } | null
): boolean {
  return engagement?.kind === 'standard' && engagement?.status === 'active';
}

/**
 * The same question as a hook, together with whether it has been answered yet,
 * for a caller that has to wait on the answer rather than fail closed.
 *
 * The section route is that caller. Opening a section picks between an editor
 * and a read-only view, and there is no safe side to guess on while the
 * engagement loads: guessing unmanaged puts an editor in front of a brand whose
 * sections we make, and guessing managed takes one away from a brand that
 * builds its own.
 */
export function useManagedBrandStatus(
  orgId: string,
  clientId: string
): { managed: boolean; isLoading: boolean } {
  const { data, isLoading } = useWgClientControllerEngagement(orgId, clientId, {
    query: { enabled: !!orgId && !!clientId, retry: false, staleTime: 60_000 },
  });
  return { managed: isManagedEngagement(data), isLoading: !!isLoading };
}

/**
 * The answer alone, for the two places on the sections library that offer to
 * hand a section over to us.
 *
 * Fails closed while the answer is unknown: a card that appears and then has to
 * be taken away is worse than a card that appears a moment late.
 */
export function useManagedBrand(orgId: string, clientId: string): boolean {
  return useManagedBrandStatus(orgId, clientId).managed;
}
